import {
  ChangeEvent,
  FocusEvent,
  ForwardedRef,
  forwardRef,
  KeyboardEvent,
  memo,
  SyntheticEvent,
  useCallback,
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, TextField } from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField';

import { InputRoot } from './InputField.style';

export type InputFieldProps = Omit<TextFieldProps, 'ref' | 'fullWidth'> & {
  isClearable?: boolean;
  isReadOnly?: boolean;
  isFullWidth?: boolean;
  onClear?: (e: ChangeEvent<HTMLInputElement>) => void;
  onReset?: (e: SyntheticEvent) => void;
  value?: StringOrNumber;
  accept?: string;
};

function InputField(props: InputFieldProps, ref: ForwardedRef<HTMLInputElement>) {
  const {
    isClearable = false,
    isReadOnly = false,
    label,
    value,
    name,
    onChange,
    onClear,
    variant = 'standard',
    margin = 'none',
    isFullWidth,
    onReset,
    ...rest
  } = props;

  const onClearHandler = useCallback(() => {
    const resetEvent = {
      target: {
        name: name ?? '',
        value: '',
      },
      currentTarget: {
        name: name ?? '',
        value: '',
      },
    } as ChangeEvent<HTMLInputElement>;

    onChange?.(resetEvent);
    onClear?.(resetEvent);
  }, [name, onChange, onClear]);

  const inputProps: TextFieldProps = ((): TextFieldProps => {
    const result: TextFieldProps = {
      slotProps: {
        input: {},
        htmlInput: {
          readOnly: isReadOnly,
          name,
        },
      },
      margin,
      name,
      variant,
      label,
      autoComplete: 'off',
      onChange,
      fullWidth: isFullWidth,
      onKeyUp: (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
          onReset?.(e as unknown as FocusEvent<HTMLInputElement>);
        }
      },
      ...rest,
    };

    if (typeof value !== 'undefined') {
      result.value = `${value}`;
    }

    if (result.value) {
      if (isClearable && result.slotProps?.input) {
        result.slotProps.input = {
          ...result.slotProps.input,
          endAdornment: (
            <IconButton key="clear" onClick={onClearHandler} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          ),
        };
      }
    }

    return result;
  })();

  return (
    <InputRoot isFullWidth={isFullWidth}>
      <TextField inputRef={ref} {...inputProps} />
    </InputRoot>
  );
}

export default memo(forwardRef(InputField));
